import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Eye, MapPin, ExternalLink, Share2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { SPORT_SCHEMAS } from "@/lib/sportProfileSchema";
import PlayerFilters from "@/components/player/PlayerFilters";
import SportStatsDashboard from "@/components/player/SportStatsDashboard";
import PlayerTournamentsSection from "@/components/player/PlayerTournamentsSection";
import Paginator, { usePagination, SORT } from "@/components/Paginator";

const EMPTY_FILTERS = { q: "", sport: "", role: "", hand: "", city: "" };

export function PlayerSearch() {
  const { user, ready } = useAuth();
  const nav = useNavigate();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [items, setItems] = useState([]);

  const load = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    const qs = params.toString();
    api.get(qs ? `/players/profiles?${qs}` : "/players/profiles").then((r) => setItems(r.data));
  };

  // Paginated view — newest players (created_at desc) on page 1.
  const pp = usePagination(items, { defaultPageSize: 20, sortFn: SORT.byCreatedDesc });

  useEffect(() => {
    if (ready && !user) { nav("/players/login"); return; }
    if (ready) load();
  }, [ready, user]);

  return (
    <div className="bg-[#0a0a0a] min-h-screen text-white">
      <Nav />
      <div className="max-w-6xl mx-auto px-6 pt-12 pb-24">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#84CC16]">/ Players</div>
        <h1 className="font-display text-5xl tracking-wide mt-2">
          {user?.role === "company_admin" ? "YOUR COMPANY PLAYERS" : "FIND PLAYERS"}
        </h1>
        <p className="text-neutral-400 mt-2 text-sm">
          {user?.role === "company_admin"
            ? `Search across every player who has set their company to ${user?.companyName || "your company"} — filter by sport, role and playing style.`
            : "Search across every player registered on Kreeda Nation — filter by sport, role and playing style."}
        </p>

        <PlayerFilters filters={filters} setFilters={setFilters} onSearch={load} />

        <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mt-4">
          / {items.length} player{items.length === 1 ? "" : "s"} match
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          {pp.view.map((p) => (
            <PlayerCard key={p.id} p={p} sportFilter={filters.sport} />
          ))}
          {items.length === 0 && <div className="col-span-full text-center text-neutral-500 py-20">No players match.</div>}
        </div>
        <Paginator {...pp.controls} label="players" />
      </div>
      <Footer />
    </div>
  );
}

/** Card on the search grid. Highlights the role for the filtered sport (or cricket default). */
function PlayerCard({ p, sportFilter }) {
  const sport = sportFilter || p.interested_sports?.[0] || "cricket";
  const schema = SPORT_SCHEMAS[sport];
  const sp = p.sport_profiles?.[sport] || {};
  // Pick a "primary role" label: schema role-like field, or legacy cricket role for cricket.
  let primary = sp.role || sp.position || sp.specialty || sp.domain;
  if (!primary && sport === "cricket") primary = p.role;
  const accent = schema?.color || "#84CC16";
  return (
    <Link to={`/players/profiles/${p.id}`} data-testid={`player-search-card-${p.id}`}
      className="border border-white/10 rounded-sm bg-[#141414] p-4 hover-lift block">
      <div className="flex items-center gap-3">
        <img src={p.photo_url || "https://images.pexels.com/photos/2216610/pexels-photo-2216610.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"} className="w-14 h-14 rounded-sm object-cover" alt="" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">{p.name}</div>
          <div className="text-[10px] font-mono uppercase text-neutral-500 truncate flex items-center gap-1.5">
            <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: accent }} />
            {primary ? primary.replace(/-/g, " ") : "any"} · {p.city || "—"}
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-sm text-[#84CC16]">{p.view_count || 0}</div>
          <div className="text-[10px] font-mono text-neutral-500 uppercase flex items-center gap-1 justify-end"><Eye className="w-3 h-3" /> views</div>
        </div>
      </div>
      {p.company_name && <div className="text-[10px] font-mono text-neutral-400 mt-2 uppercase tracking-widest">@ {p.company_name}</div>}
      {/* Sport tags */}
      {(p.interested_sports?.length > 0) && (
        <div className="flex flex-wrap gap-1 mt-3">
          {p.interested_sports.slice(0, 5).map((s) => (
            <span key={s} className="text-[9px] font-mono uppercase border border-white/10 rounded-sm px-1.5 py-0.5"
              style={{ color: SPORT_SCHEMAS[s]?.color || "#84CC16" }}>
              {SPORT_SCHEMAS[s]?.label || s}
            </span>
          ))}
        </div>
      )}
    </Link>
  );
}

export function PlayerProfileView() {
  const { id, slug } = useParams();
  const { user, ready } = useAuth();
  const nav = useNavigate();
  const [p, setP] = useState(null);

  useEffect(() => {
    // Public profile — anonymous viewers welcome (backend redacts mobile/email).
    // Two entry points: `/players/profiles/:id` (UUID) or `/p/:slug` (pretty share URL).
    if (!ready) return;
    const req = slug
      ? api.get(`/players/by-slug/${slug}`)
      : api.get(`/players/profiles/${id}`);
    req.then((r) => setP(r.data)).catch(() => setP(null));
  }, [ready, id, slug]);

  if (!p) return <div className="bg-[#0a0a0a] min-h-screen text-white"><Nav /><div className="p-20 text-center">Loading…</div></div>;

  return (
    <div className="bg-[#0a0a0a] min-h-screen text-white">
      <Nav />
      <div className="max-w-5xl mx-auto px-6 pt-12 pb-24">
        <Link to="/players/profiles" className="text-xs font-mono text-neutral-400 hover:text-white">← Back to players</Link>

        <div className="mt-6 grid md:grid-cols-2 gap-10">
          <div className="border border-white/10 rounded-sm overflow-hidden bg-[#141414] aspect-square relative">
            <img src={p.photo_url || "https://images.pexels.com/photos/2216610/pexels-photo-2216610.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"} alt="" className="w-full h-full object-cover" />
            {p.jersey_number != null && <span className="absolute top-5 right-5 font-mono text-7xl text-white/90">#{p.jersey_number}</span>}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black to-transparent p-6">
              <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-300">{p.company_name || "Independent"}</div>
              <div className="font-display text-4xl tracking-wide">{p.name}</div>
            </div>
          </div>

          <div>
            <div className="flex items-end justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#84CC16]">/ Player</div>
                <h1 data-testid="player-profile-name" className="font-display text-5xl tracking-wide mt-2">{p.name}</h1>
              </div>
              <div className="text-right">
                <div className="font-display text-4xl text-[#84CC16]" data-testid="player-profile-views">{p.view_count || 0}</div>
                <div className="text-[10px] font-mono uppercase text-neutral-500 tracking-widest flex items-center gap-1 justify-end"><Eye className="w-3 h-3" /> Views</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-px bg-white/10 mt-8 border border-white/10 rounded-sm overflow-hidden">
              {[
                ["JERSEY", p.jersey_number ?? "—"],
                ["CITY", p.city || "—"],
                ["MOBILE", p.mobile_masked || p.mobile || "—"],
                ["DOB", p.dob || "—"],
              ].map(([l, v]) => (
                <div key={l} className="bg-[#0a0a0a] p-4">
                  <div className="font-mono text-sm">{v}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mt-1">{l}</div>
                </div>
              ))}
            </div>

            <PrettyShareRow slug={p.slug} name={p.name} />

            {/* Sport-specific cards — one per interested sport */}
            <SportCards p={p} />

            {/* Career stats dashboard */}
            <div className="mt-6">
              <SportStatsDashboard profileId={p.id} interestedSports={p.interested_sports?.length ? p.interested_sports : ["cricket"]} />
            </div>

            {/* Tournaments hosted + played */}
            <PlayerTournamentsSection profileId={p.id} isOwner={false} playerName={p.name || ""} />

            {p.bio && <p className="mt-6 text-neutral-300 leading-relaxed">{p.bio}</p>}

            {p.cricheroes_url && (
              <a href={p.cricheroes_url} target="_blank" rel="noopener noreferrer" data-testid="player-cricheroes-link"
                className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-[#84CC16]/40 text-[#84CC16] hover:bg-[#84CC16]/10 text-sm font-mono uppercase tracking-widest">
                <ExternalLink className="w-4 h-4" /> Cric Heroes profile
              </a>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

/** Copy-to-clipboard row for the player's pretty share URL (kreedanation.com/p/xxx).
 * Falls back to the UUID URL when the profile has no slug (very old rows only). */
function PrettyShareRow({ slug, name }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/p/${slug || ""}`;
  const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };
  const onShare = async () => {
    try {
      await navigator.share({ title: `${name} · Kreeda Nation`, url });
    } catch {
      /* user cancelled */
    }
  };
  if (!slug) return null;
  return (
    <div className="mt-6 border border-white/10 rounded-sm bg-[#0f0f0f] p-3" data-testid="pretty-share-row">
      <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1.5">/ Share this player card</div>
      <div className="flex gap-2 flex-wrap">
        <div className="flex-1 min-w-0 bg-black/40 border border-white/10 rounded-sm px-3 py-2 font-mono text-xs text-[#84CC16] truncate" data-testid="pretty-share-url">
          {url}
        </div>
        <Button onClick={onCopy} data-testid="pretty-share-copy" size="sm" className="bg-[#84CC16] hover:bg-[#65A30D] text-black rounded-sm font-semibold">
          {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
          {copied ? "Copied" : "Copy"}
        </Button>
        {canNativeShare && (
          <Button onClick={onShare} data-testid="pretty-share-native" size="sm" variant="outline" className="border-white/10 rounded-sm">
            <Share2 className="w-4 h-4 mr-1" /> Share
          </Button>
        )}
      </div>
    </div>
  );
}

/** Renders one card per sport the player is interested in. Falls back to legacy
 * cricket fields for players who haven't migrated to multi-sport yet. */
function SportCards({ p }) {
  const interested = p.interested_sports?.length ? p.interested_sports : ["cricket"];
  const sportProfiles = p.sport_profiles || {};

  // Legacy fallback so old cricket-only profiles still render meaningfully.
  const legacyCricket = {
    role: p.role || "any",
    batting_hand: p.batting_hand || "right",
    bowling_style: p.bowling_style || "none",
    jersey_number: p.jersey_number,
    cricheroes_url: p.cricheroes_url,
  };

  return (
    <div className="mt-6 space-y-3" data-testid="player-sport-cards">
      {interested.map((sport) => {
        const schema = SPORT_SCHEMAS[sport];
        if (!schema) return null;
        const sp = (sport === "cricket" && !sportProfiles.cricket) ? legacyCricket : (sportProfiles[sport] || {});
        const rows = schema.fields
          .filter((f) => sp[f.key] !== undefined && sp[f.key] !== null && sp[f.key] !== "")
          .map((f) => [f.label.toUpperCase(), String(sp[f.key]).replace(/-/g, " ")]);
        return (
          <div key={sport} data-testid={`player-card-${sport}`} className="border border-white/10 rounded-sm bg-black/30 p-4">
            <div className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: schema.color }} />
              <div className="font-display tracking-wider text-lg">{schema.label.toUpperCase()}</div>
            </div>
            {rows.length === 0 ? (
              <div className="text-xs font-mono text-neutral-500 mt-2">No details yet.</div>
            ) : (
              <div className="grid grid-cols-2 gap-px bg-white/5 mt-3 border border-white/10 rounded-sm overflow-hidden">
                {rows.map(([l, v]) => (
                  <div key={l} className="bg-[#0a0a0a] p-3">
                    <div className="font-mono text-sm">{v}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mt-1">{l}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

